import { useState, useEffect } from 'react';
import { Save, Shield, Key, Mail, CreditCard, Eye, EyeOff, Wallet, CheckCircle, AlertCircle, Loader2, Database, Rocket, Bell, MessageSquare, Phone, Gift, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import TwoFactorAuth from '@/components/admin/TwoFactorAuth';
import LockedAccountsTab from '@/components/admin/LockedAccountsTab';
import BlockedUsersTab from '@/components/admin/BlockedUsersTab';

import { API_URL } from '@/config/api';

// ── Production Setup Component ──
function ProductionSetupTab({ token, apiUrl }) {
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [menuCount, setMenuCount] = useState(null);
  const [sidesCount, setSidesCount] = useState(null);
  const [addrsCount, setAddrsCount] = useState(null);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const [menuRes, sidesRes, addrsRes] = await Promise.all([
        fetch(`${apiUrl}/api/menu`),
        fetch(`${apiUrl}/api/sides`),
        fetch(`${apiUrl}/api/delivery-addresses`)
      ]);
      if (menuRes.ok) setMenuCount((await menuRes.json()).length);
      if (sidesRes.ok) setSidesCount((await sidesRes.json()).length);
      if (addrsRes.ok) setAddrsCount((await addrsRes.json()).length);
    } catch {}
  };

  const runSeed = async (force = false) => {
    setSeeding(true);
    setSeedResult(null);
    try {
      const r = await fetch(`${apiUrl}/api/admin/menu/seed-full-menu?force=${force}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await r.json();
      setSeedResult(data);
      if (r.ok) {
        toast.success(data.message);
        checkStatus();
      } else {
        toast.error(data.detail || 'Seed failed');
      }
    } catch (e) {
      toast.error('Failed to run seed');
    } finally {
      setSeeding(false);
    }
  };

  const steps = [
    {
      step: 1,
      title: 'Set MongoDB Atlas URL',
      description: 'In your Emergent production deployment settings, set MONGO_URL to your MongoDB Atlas connection string.',
      code: 'MONGO_URL=mongodb+srv://user:password@cluster.mongodb.net/gc3h_canteen',
      docs: 'https://cloud.mongodb.com → Create Cluster → Connect → Drivers → Copy connection string',
      done: null // Can't verify from frontend
    },
    {
      step: 2,
      title: 'Deploy the App',
      description: 'Push your code to deploy. The backend will connect to MongoDB Atlas automatically.',
      done: null
    },
    {
      step: 3,
      title: 'Seed Full Menu & Configuration',
      description: 'Run this once after deploying to populate your production database with the complete Rosond menu (52 items + 6 sides + delivery addresses + platform fees).',
      done: menuCount >= 24,
      action: true
    },
    {
      step: 4,
      title: 'Verify Menu Live',
      description: 'Check that the menu appears correctly on the customer app.',
      done: menuCount >= 24
    }
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Rocket className="h-5 w-5 text-primary" />
            Production Go-Live Checklist
          </CardTitle>
          <CardDescription>
            Follow these steps to deploy GC3H Online Canteen to production (gc3honlinecanteen.co.za)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Status Row */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className={`p-3 rounded-xl border text-center ${menuCount >= 24 ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
              <p className="text-2xl font-black">{menuCount ?? '—'}</p>
              <p className="text-xs text-gray-600 mt-0.5">Menu Items</p>
              <p className="text-xs font-medium mt-0.5">{menuCount >= 24 ? '✅ Ready' : '⚠ Seed needed'}</p>
            </div>
            <div className={`p-3 rounded-xl border text-center ${sidesCount >= 6 ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
              <p className="text-2xl font-black">{sidesCount ?? '—'}</p>
              <p className="text-xs text-gray-600 mt-0.5">Sides</p>
              <p className="text-xs font-medium mt-0.5">{sidesCount >= 6 ? '✅ Ready' : '⚠ Seed needed'}</p>
            </div>
            <div className={`p-3 rounded-xl border text-center ${addrsCount >= 1 ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
              <p className="text-2xl font-black">{addrsCount ?? '—'}</p>
              <p className="text-xs text-gray-600 mt-0.5">Locations</p>
              <p className="text-xs font-medium mt-0.5">{addrsCount >= 1 ? '✅ Ready' : '⚠ Seed needed'}</p>
            </div>
          </div>

          {/* Steps */}
          <div className="space-y-4">
            {steps.map((s) => (
              <div key={s.step} className={`p-4 rounded-xl border-l-4 ${s.done === true ? 'border-l-green-500 bg-green-50' : s.done === false ? 'border-l-amber-500 bg-amber-50' : 'border-l-gray-300 bg-gray-50'}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold ${s.done === true ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700'}`}>
                    {s.done === true ? '✓' : s.step}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900">{s.title}</p>
                    <p className="text-sm text-gray-600 mt-0.5">{s.description}</p>
                    {s.code && (
                      <div className="mt-2 p-2 bg-gray-800 rounded-lg">
                        <code className="text-xs text-green-400 font-mono break-all">{s.code}</code>
                      </div>
                    )}
                    {s.docs && (
                      <p className="text-xs text-blue-600 mt-1">{s.docs}</p>
                    )}
                    {s.action && (
                      <div className="mt-3 flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          onClick={() => runSeed(false)}
                          disabled={seeding}
                          className="bg-primary"
                          data-testid="seed-production-btn"
                        >
                          {seeding ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Database className="h-4 w-4 mr-1" />}
                          {seeding ? 'Seeding...' : 'Seed Database'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => runSeed(true)}
                          disabled={seeding}
                        >
                          Force Re-seed (update all)
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Seed Result */}
          {seedResult && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <p className="font-semibold text-blue-900 mb-2">Seed Result:</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>Created: <span className="font-bold">{seedResult.created}</span></div>
                <div>Updated: <span className="font-bold">{seedResult.updated}</span></div>
                <div>Sides: <span className="font-bold">{seedResult.sides_seeded}</span></div>
                <div>Addresses: <span className="font-bold">{seedResult.delivery_addresses_seeded}</span></div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* MongoDB Atlas Guide */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            MongoDB Atlas Setup Guide
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="space-y-2">
            {[
              '1. Go to cloud.mongodb.com and create a free account',
              '2. Create a new cluster (free tier M0 is enough)',
              '3. Under Security → Database Access: create a user with read/write access',
              '4. Under Security → Network Access: allow access from anywhere (0.0.0.0/0)',
              '5. Under Deployment → Database: click Connect → Drivers → Copy connection string',
              '6. Replace <password> in the string with your database user password',
              '7. In Emergent production deployment settings: add MONGO_URL = <your connection string>',
              '8. Deploy the app, then come here and click "Seed Database"'
            ].map((step, i) => (
              <p key={i} className={`p-2 rounded ${i === 6 ? 'bg-amber-50 border border-amber-200 font-semibold text-amber-800' : 'text-gray-700'}`}>{step}</p>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


// ── Notification Settings Component ──
function NotificationSettingsTab({ token }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testing, setTesting] = useState(false);
  const [testWaPhone, setTestWaPhone] = useState('');
  const [testingWa, setTestingWa] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/notification-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
      });
      if (r.ok) setSettings(await r.json());
    } catch (e) { toast.error('Failed to load notification settings'); }
    finally { setLoading(false); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/notification-settings`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(settings)
      });
      if (!r.ok) throw new Error('Failed to save');
      toast.success('Notification settings saved');
      fetchSettings();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const sendTestSMS = async () => {
    if (!testPhone) { toast.error('Enter a phone number'); return; }
    setTesting(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/notification-settings/test-sms`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ phone_number: testPhone })
      });
      const data = await r.json();
      if (data.success) toast.success('Test SMS sent!');
      else toast.error(data.error || 'Failed to send test SMS');
    } catch (e) { toast.error('Failed to send test SMS'); }
    finally { setTesting(false); }
  };

  const sendTestWhatsApp = async () => {
    if (!testWaPhone) { toast.error('Enter a WhatsApp number'); return; }
    setTestingWa(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/notification-settings/test-whatsapp`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ phone_number: testWaPhone })
      });
      const data = await r.json();
      if (data.success) toast.success('Test WhatsApp sent!');
      else toast.error(data.error || 'Failed to send test WhatsApp — save settings first');
    } catch (e) { toast.error('Failed to send test WhatsApp'); }
    finally { setTestingWa(false); }
  };

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const s = settings || { email_enabled: true, sms_enabled: false, whatsapp_enabled: false, twilio: {} };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Bell className="h-5 w-5" />Notification Channels</CardTitle>
          <CardDescription>Enable or disable notification channels for order updates</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {[
            { key: 'email_enabled', icon: Mail, color: 'blue', label: 'Email Notifications', desc: 'Order confirmations, status updates via email' },
            { key: 'sms_enabled', icon: Phone, color: 'green', label: 'SMS Notifications (Twilio)', desc: 'Order updates via SMS to customer phone' },
            { key: 'whatsapp_enabled', icon: MessageSquare, color: 'emerald', label: 'WhatsApp Notifications', desc: 'Order updates via WhatsApp (Meta or Twilio)' },
          ].map(({ key, icon: Icon, color, label, desc }) => (
            <div key={key} className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 bg-${color}-100 rounded-lg flex items-center justify-center`}><Icon className={`h-5 w-5 text-${color}-600`} /></div>
                <div><p className="font-medium">{label}</p><p className="text-sm text-muted-foreground">{desc}</p></div>
              </div>
              <button onClick={() => setSettings(p => ({ ...p, [key]: !p?.[key] }))}
                className={`w-12 h-6 rounded-full transition-colors ${s[key] ? 'bg-green-500' : 'bg-gray-300'}`}
                data-testid={`toggle-${key}`}>
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${s[key] ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
          ))}
        </CardContent>
      </Card>

      {s.sms_enabled && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Phone className="h-5 w-5" />Twilio SMS Configuration</CardTitle>
            <CardDescription>Get credentials from <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="text-primary underline">console.twilio.com</a></CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div><Label>Account SID</Label><Input value={s.twilio?.account_sid || ''} onChange={(e) => setSettings(p => ({ ...p, twilio: { ...p.twilio, account_sid: e.target.value } }))} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" data-testid="twilio-sid" /></div>
            <div><Label>Auth Token</Label><Input type="password" value={s.twilio?.auth_token || ''} onChange={(e) => setSettings(p => ({ ...p, twilio: { ...p.twilio, auth_token: e.target.value } }))} placeholder="Enter auth token" data-testid="twilio-token" /></div>
            <div><Label>Twilio Phone Number</Label><Input value={s.twilio?.phone_number || ''} onChange={(e) => setSettings(p => ({ ...p, twilio: { ...p.twilio, phone_number: e.target.value } }))} placeholder="+27xxxxxxxxx" data-testid="twilio-phone" /></div>
            <div className="border-t pt-4 mt-4">
              <Label>SMS Usage Scope</Label>
              <p className="text-xs text-muted-foreground mb-2">Verification codes and password-reset links are always sent by SMS when configured. Choose whether order updates also go by SMS (costs more).</p>
              <div className="space-y-2">
                {[
                  { value: 'verification_only', label: 'Verification only (recommended)', desc: 'OTP codes & password resets — keeps SMS costs low' },
                  { value: 'all', label: 'All notifications', desc: 'Also send order updates by SMS to customers who prefer it' },
                ].map(opt => (
                  <button key={opt.value} onClick={() => setSettings(p => ({ ...p, sms_scope: opt.value }))}
                    className={`w-full text-left p-3 border rounded-lg transition-colors ${(s.sms_scope || 'verification_only') === opt.value ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : 'border-border hover:border-green-300'}`}
                    data-testid={`sms-scope-${opt.value}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${(s.sms_scope || 'verification_only') === opt.value ? 'border-green-500' : 'border-gray-300'}`}>
                        {(s.sms_scope || 'verification_only') === opt.value && <div className="w-2 h-2 rounded-full bg-green-500" />}
                      </div>
                      <span className="font-medium text-sm">{opt.label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground ml-6">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>
            <div className="border-t pt-4 mt-4">
              <Label>Send Test SMS</Label>
              <div className="flex gap-2 mt-1">
                <Input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="+27xxxxxxxxx" data-testid="test-phone" />
                <Button onClick={sendTestSMS} disabled={testing} variant="outline" data-testid="send-test-sms">
                  {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send Test'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {s.whatsapp_enabled && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><MessageSquare className="h-5 w-5" />WhatsApp Configuration</CardTitle>
            <CardDescription>Used for phone OTP verification, password-reset links &amp; order updates.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Provider</Label>
              <select
                value={s.whatsapp_provider || 'meta'}
                onChange={(e) => setSettings(p => ({ ...p, whatsapp_provider: e.target.value }))}
                className="mt-1 w-full border rounded-md h-10 px-3 bg-background text-sm"
                data-testid="whatsapp-provider">
                <option value="meta">Meta WhatsApp Cloud API</option>
                <option value="twilio">Twilio WhatsApp</option>
              </select>
            </div>

            {(s.whatsapp_provider || 'meta') === 'twilio' ? (
              <>
                <div><Label>Twilio Account SID</Label><Input value={s.twilio?.account_sid || ''} onChange={(e) => setSettings(p => ({ ...p, twilio: { ...p.twilio, account_sid: e.target.value } }))} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" data-testid="wa-twilio-sid" /></div>
                <div><Label>Twilio Auth Token</Label><Input type="password" value={s.twilio?.auth_token || ''} onChange={(e) => setSettings(p => ({ ...p, twilio: { ...p.twilio, auth_token: e.target.value } }))} placeholder="Enter auth token" data-testid="wa-twilio-token" /></div>
                <div><Label>WhatsApp Sender Number</Label><Input value={s.twilio?.whatsapp_from || ''} onChange={(e) => setSettings(p => ({ ...p, twilio: { ...p.twilio, whatsapp_from: e.target.value } }))} placeholder="+14155238886 (or your approved WA number)" data-testid="wa-twilio-from" /><p className="text-xs text-muted-foreground mt-1">Your Twilio WhatsApp sender (sandbox or approved business number), in +E.164 format.</p></div>
                <div><Label>OTP Template Content SID <span className="text-muted-foreground">(recommended for production)</span></Label><Input value={s.twilio?.whatsapp_otp_template_sid || ''} onChange={(e) => setSettings(p => ({ ...p, twilio: { ...p.twilio, whatsapp_otp_template_sid: e.target.value } }))} placeholder="HXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" data-testid="wa-twilio-template" /><p className="text-xs text-muted-foreground mt-1">An approved WhatsApp authentication template (variable {'{{1}}'} = code). Required to deliver OTPs to users outside the 24-hour session window.</p></div>
                <div className="border-t pt-4 mt-2">
                  <Label>Send Test WhatsApp</Label>
                  <div className="flex gap-2 mt-1">
                    <Input value={testWaPhone} onChange={(e) => setTestWaPhone(e.target.value)} placeholder="+27xxxxxxxxx" data-testid="test-wa-phone" />
                    <Button onClick={sendTestWhatsApp} disabled={testingWa} variant="outline" data-testid="send-test-whatsapp">
                      {testingWa ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send Test'}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Save settings before testing. For the sandbox, the recipient must first join your Twilio WhatsApp sandbox.</p>
                </div>
              </>
            ) : (
              <>
                <div><Label>Meta Phone Number ID</Label><Input value={s.meta_whatsapp?.phone_number_id || ''} onChange={(e) => setSettings(p => ({ ...p, meta_whatsapp: { ...p.meta_whatsapp, phone_number_id: e.target.value } }))} placeholder="From Meta Business → WhatsApp" data-testid="wa-meta-phone-id" /></div>
                <div><Label>Meta Access Token</Label><Input type="password" value={s.meta_whatsapp?.access_token || ''} onChange={(e) => setSettings(p => ({ ...p, meta_whatsapp: { ...p.meta_whatsapp, access_token: e.target.value } }))} placeholder="Permanent access token" data-testid="wa-meta-token" /></div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Button onClick={saveSettings} disabled={saving} className="w-full" data-testid="save-notification-settings">
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
        Save Notification Settings
      </Button>
    </div>
  );
}




export default function AdminSettings() {
  const { token, user } = useAuth();
  const [settings, setSettings] = useState({
    hero_title: '',
    hero_subtitle: '',
    hero_image: '',
    logo_url: '',
    about_text: '',
    contact_email: '',
    contact_phone: '',
    contact_address: ''
  });
  const [systemConfig, setSystemConfig] = useState({
    default_payment_provider: 'paystack',
    paystack_secret_key: '',
    paystack_public_key: '',
    stripe_api_key: '',
    stripe_webhook_secret: '',
    resend_api_key: '',
    resend_sender_email: '',
    daily_report_enabled: false,
    daily_report_hour: 18,
    voucher_campaign_enabled: true,
    voucher_campaign_hour: 7,
    weekly_reminder_enabled: true,
    weekly_reminder_hour: 7
  });
  const [showKeys, setShowKeys] = useState({
    paystack_secret_key: false,
    paystack_public_key: false,
    stripe_api_key: false,
    stripe_webhook_secret: false,
    resend_api_key: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [webhookHealth, setWebhookHealth] = useState(null);
  const [sendingReport, setSendingReport] = useState(false);
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const [sendingWeekly, setSendingWeekly] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    fetchSettings();
    if (isSuperAdmin) {
      fetchSystemConfig();
      fetch(`${API_URL}/api/admin/webhook/health`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' })
        .then(r => r.ok ? r.json() : null).then(d => setWebhookHealth(d)).catch(() => {});
    }
  }, [token, isSuperAdmin]);

  const handleSendReportNow = async () => {
    setSendingReport(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/reports/send-daily-now`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success(`Report sent — ${d.summary?.orders ?? 0} orders, R${(d.summary?.revenue ?? 0).toFixed(0)} revenue today`);
    } catch (e) { toast.error(e.message); }
    finally { setSendingReport(false); }
  };

  const handleSendCampaignNow = async () => {
    if (!window.confirm('Send a "Gift a voucher" nudge to ALL customers now (email + WhatsApp for opted-in)?')) return;
    setSendingCampaign(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/vouchers/send-campaign-now`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include', body: JSON.stringify({ label: 'Special' })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message || 'Campaign sent');
    } catch (e) { toast.error(e.message); }
    finally { setSendingCampaign(false); }
  };

  const handleSendWeeklyNow = async () => {
    if (!window.confirm("Send this week's menu to ALL opted-in customers now (email + WhatsApp)?")) return;
    setSendingWeekly(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/marketing/send-weekly-reminder-now`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message || 'Weekly reminder sent');
    } catch (e) { toast.error(e.message); }
    finally { setSendingWeekly(false); }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/system-config`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSystemConfig({
          default_payment_provider: data.default_payment_provider || 'paystack',
          paystack_secret_key: '',
          paystack_public_key: '',
          stripe_api_key: '',
          stripe_webhook_secret: '',
          resend_api_key: '',
          resend_sender_email: data.resend_sender_email || '',
          daily_report_enabled: !!data.daily_report_enabled,
          daily_report_hour: data.daily_report_hour ?? 18,
          voucher_campaign_enabled: data.voucher_campaign_enabled !== false,
          voucher_campaign_hour: data.voucher_campaign_hour ?? 7,
          weekly_reminder_enabled: data.weekly_reminder_enabled !== false,
          weekly_reminder_hour: data.weekly_reminder_hour ?? 7,
          paystack_secret_key_masked: data.paystack_secret_key_masked || '',
          paystack_public_key_masked: data.paystack_public_key_masked || '',
          stripe_api_key_masked: data.stripe_api_key_masked || '',
          stripe_webhook_secret_masked: data.stripe_webhook_secret_masked || '',
          resend_api_key_masked: data.resend_api_key_masked || ''
        });
      }
    } catch (error) {
      console.error('Error fetching system config:', error);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleConfigChange = (field, value) => {
    setSystemConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(settings)
      });

      if (!response.ok) throw new Error('Failed to save settings');

      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      // Only send non-empty values
      const configToSave = {};
      if (systemConfig.default_payment_provider) configToSave.default_payment_provider = systemConfig.default_payment_provider;
      if (systemConfig.paystack_secret_key) configToSave.paystack_secret_key = systemConfig.paystack_secret_key;
      if (systemConfig.paystack_public_key) configToSave.paystack_public_key = systemConfig.paystack_public_key;
      if (systemConfig.stripe_api_key) configToSave.stripe_api_key = systemConfig.stripe_api_key;
      if (systemConfig.stripe_webhook_secret) configToSave.stripe_webhook_secret = systemConfig.stripe_webhook_secret;
      if (systemConfig.resend_api_key) configToSave.resend_api_key = systemConfig.resend_api_key;
      if (systemConfig.resend_sender_email) configToSave.resend_sender_email = systemConfig.resend_sender_email;
      configToSave.daily_report_enabled = !!systemConfig.daily_report_enabled;
      configToSave.daily_report_hour = Number(systemConfig.daily_report_hour) || 18;
      configToSave.voucher_campaign_enabled = !!systemConfig.voucher_campaign_enabled;
      configToSave.voucher_campaign_hour = Number(systemConfig.voucher_campaign_hour) || 7;
      configToSave.weekly_reminder_enabled = !!systemConfig.weekly_reminder_enabled;
      configToSave.weekly_reminder_hour = Math.max(0, Math.min(23, parseInt(systemConfig.weekly_reminder_hour) || 0));

      const response = await fetch(`${API_URL}/api/admin/system-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(configToSave)
      });

      if (!response.ok) throw new Error('Failed to save system configuration');

      toast.success('System configuration saved successfully');
      
      // Clear input fields and refresh masked values
      setSystemConfig(prev => ({
        ...prev,
        paystack_secret_key: '',
        paystack_public_key: '',
        stripe_api_key: '',
        stripe_webhook_secret: '',
        resend_api_key: ''
      }));
      fetchSystemConfig();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingConfig(false);
    }
  };

  const toggleShowKey = (field) => {
    setShowKeys(prev => ({ ...prev, [field]: !prev[field] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="admin-settings">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-semibold text-secondary-900">Settings</h1>
        <p className="text-muted-foreground">Manage site settings and system configuration</p>
      </div>

      <Tabs defaultValue="site" className="space-y-6">
        <TabsList>
          <TabsTrigger value="site">Site Settings</TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="system" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              System Config
            </TabsTrigger>
          )}
          {isSuperAdmin && (
            <TabsTrigger value="production" className="flex items-center gap-2">
              <Key className="h-4 w-4" />
              Production Setup
            </TabsTrigger>
          )}
          {isSuperAdmin && (
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Notifications
            </TabsTrigger>
          )}
          {isSuperAdmin && (
            <TabsTrigger value="security" className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4" />
              Security
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="site">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Hero Section */}
            <Card>
              <CardHeader>
                <CardTitle>Hero Section</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="hero_title">Hero Title</Label>
                  <Input
                    id="hero_title"
                    value={settings.hero_title}
                    onChange={(e) => handleChange('hero_title', e.target.value)}
                    placeholder="GC3H Halaal Catering"
                    data-testid="hero-title"
                  />
                </div>
                <div>
                  <Label htmlFor="hero_subtitle">Hero Subtitle</Label>
                  <Input
                    id="hero_subtitle"
                    value={settings.hero_subtitle}
                    onChange={(e) => handleChange('hero_subtitle', e.target.value)}
                    placeholder="Order Smart. Eat Fresh. Delivered Right."
                    data-testid="hero-subtitle"
                  />
                </div>
                <div>
                  <Label htmlFor="hero_image">Hero Image URL</Label>
                  <Input
                    id="hero_image"
                    value={settings.hero_image}
                    onChange={(e) => handleChange('hero_image', e.target.value)}
                    placeholder="https://..."
                    data-testid="hero-image"
                  />
                  {settings.hero_image && (
                    <img src={settings.hero_image} alt="Hero preview" className="mt-2 h-32 w-full object-cover rounded" />
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Branding */}
            <Card>
              <CardHeader>
                <CardTitle>Branding</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="logo_url">Logo URL</Label>
                  <Input
                    id="logo_url"
                    value={settings.logo_url}
                    onChange={(e) => handleChange('logo_url', e.target.value)}
                    placeholder="https://..."
                    data-testid="logo-url"
                  />
                  {settings.logo_url && (
                    <img src={settings.logo_url} alt="Logo preview" className="mt-2 h-16 object-contain" />
                  )}
                </div>
                <div>
                  <Label htmlFor="about_text">About Text</Label>
                  <Textarea
                    id="about_text"
                    value={settings.about_text}
                    onChange={(e) => handleChange('about_text', e.target.value)}
                    placeholder="Tell customers about GC3H..."
                    rows={4}
                    data-testid="about-text"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="contact_email">Email</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={settings.contact_email}
                      onChange={(e) => handleChange('contact_email', e.target.value)}
                      placeholder="info@gc3h.co.za"
                      data-testid="contact-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_phone">Phone</Label>
                    <Input
                      id="contact_phone"
                      value={settings.contact_phone}
                      onChange={(e) => handleChange('contact_phone', e.target.value)}
                      placeholder="011 266 3181"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_address">Address</Label>
                    <Input
                      id="contact_address"
                      value={settings.contact_address}
                      onChange={(e) => handleChange('contact_address', e.target.value)}
                      placeholder="19 Richards Drive, Midrand"
                      data-testid="contact-address"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-6">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-primary hover:bg-primary-600"
              data-testid="save-settings-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </TabsContent>

        {isSuperAdmin && (
          <TabsContent value="system">
            <div className="space-y-6">
              {/* 2FA Section */}
              <TwoFactorAuth token={token} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Paystack Configuration (Default) */}
              <Card className="border-2 border-primary/20">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Wallet className="h-5 w-5 text-primary" />
                      Paystack Payment Gateway
                    </CardTitle>
                    <Badge className="bg-primary text-white">Default</Badge>
                  </div>
                  <CardDescription>
                    Primary payment processor for South African Rand (ZAR) transactions
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="paystack_public_key">Public Key</Label>
                    <div className="relative">
                      <Input
                        id="paystack_public_key"
                        type={showKeys.paystack_public_key ? 'text' : 'password'}
                        value={systemConfig.paystack_public_key}
                        onChange={(e) => handleConfigChange('paystack_public_key', e.target.value)}
                        placeholder={systemConfig.paystack_public_key_masked || 'pk_live_...'}
                        className="pr-10"
                        data-testid="paystack-public-key"
                      />
                      <button
                        type="button"
                        onClick={() => toggleShowKey('paystack_public_key')}
                        className="absolute right-3 top-1/2 -translate-y-1/2"
                      >
                        {showKeys.paystack_public_key ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </button>
                    </div>
                    {systemConfig.paystack_public_key_masked && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Current: {systemConfig.paystack_public_key_masked}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="paystack_secret_key">Secret Key</Label>
                    <div className="relative">
                      <Input
                        id="paystack_secret_key"
                        type={showKeys.paystack_secret_key ? 'text' : 'password'}
                        value={systemConfig.paystack_secret_key}
                        onChange={(e) => handleConfigChange('paystack_secret_key', e.target.value)}
                        placeholder={systemConfig.paystack_secret_key_masked || 'sk_live_...'}
                        className="pr-10"
                        data-testid="paystack-secret-key"
                      />
                      <button
                        type="button"
                        onClick={() => toggleShowKey('paystack_secret_key')}
                        className="absolute right-3 top-1/2 -translate-y-1/2"
                      >
                        {showKeys.paystack_secret_key ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </button>
                    </div>
                    {systemConfig.paystack_secret_key_masked && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Current: {systemConfig.paystack_secret_key_masked}
                      </p>
                    )}
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                    <strong>Webhook URL:</strong> Configure in Paystack Dashboard → Settings → API Keys & Webhooks
                    <br />
                    <code className="text-xs bg-amber-100 px-1 rounded">{window.location.origin}/api/webhook/paystack</code>
                  </div>
                </CardContent>
              </Card>

              {/* Stripe Configuration (Legacy) */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-slate-500" />
                      Stripe Payment Gateway
                    </CardTitle>
                    <Badge variant="outline">Legacy</Badge>
                  </div>
                  <CardDescription>
                    Backup payment processor (used if Paystack is not configured)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="stripe_api_key">API Key (Secret Key)</Label>
                    <div className="relative">
                      <Input
                        id="stripe_api_key"
                        type={showKeys.stripe_api_key ? 'text' : 'password'}
                        value={systemConfig.stripe_api_key}
                        onChange={(e) => handleConfigChange('stripe_api_key', e.target.value)}
                        placeholder={systemConfig.stripe_api_key_masked || 'sk_live_...'}
                        className="pr-10"
                        data-testid="stripe-api-key"
                      />
                      <button
                        type="button"
                        onClick={() => toggleShowKey('stripe_api_key')}
                        className="absolute right-3 top-1/2 -translate-y-1/2"
                      >
                        {showKeys.stripe_api_key ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </button>
                    </div>
                    {systemConfig.stripe_api_key_masked && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Current: {systemConfig.stripe_api_key_masked}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="stripe_webhook_secret">Webhook Secret</Label>
                    <div className="relative">
                      <Input
                        id="stripe_webhook_secret"
                        type={showKeys.stripe_webhook_secret ? 'text' : 'password'}
                        value={systemConfig.stripe_webhook_secret}
                        onChange={(e) => handleConfigChange('stripe_webhook_secret', e.target.value)}
                        placeholder={systemConfig.stripe_webhook_secret_masked || 'whsec_...'}
                        className="pr-10"
                        data-testid="stripe-webhook-secret"
                      />
                      <button
                        type="button"
                        onClick={() => toggleShowKey('stripe_webhook_secret')}
                        className="absolute right-3 top-1/2 -translate-y-1/2"
                      >
                        {showKeys.stripe_webhook_secret ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </button>
                    </div>
                    {systemConfig.stripe_webhook_secret_masked && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Current: {systemConfig.stripe_webhook_secret_masked}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Resend Email Configuration */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-primary" />
                    Resend Email Service
                  </CardTitle>
                  <CardDescription>
                    Configure email sending for notifications, order confirmations, and collection codes
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="resend_api_key">API Key</Label>
                      <div className="relative">
                        <Input
                          id="resend_api_key"
                          type={showKeys.resend_api_key ? 'text' : 'password'}
                          value={systemConfig.resend_api_key}
                          onChange={(e) => handleConfigChange('resend_api_key', e.target.value)}
                          placeholder={systemConfig.resend_api_key_masked || 're_...'}
                          className="pr-10"
                          data-testid="resend-api-key"
                        />
                        <button
                          type="button"
                          onClick={() => toggleShowKey('resend_api_key')}
                          className="absolute right-3 top-1/2 -translate-y-1/2"
                        >
                          {showKeys.resend_api_key ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                        </button>
                      </div>
                      {systemConfig.resend_api_key_masked && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Current: {systemConfig.resend_api_key_masked}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        Leave empty to use default Emergent key
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="resend_sender_email">Sender Email</Label>
                      <Input
                        id="resend_sender_email"
                        type="email"
                        value={systemConfig.resend_sender_email}
                        onChange={(e) => handleConfigChange('resend_sender_email', e.target.value)}
                        placeholder="noreply@gc3h.co.za"
                        data-testid="resend-sender-email"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {/* Scheduled Sales Report */}
              <Card data-testid="scheduled-report-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base"><Mail className="h-4 w-4" /> Daily Sales Report</CardTitle>
                  <CardDescription>Email a sales summary to all admins automatically each day.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="dr-enabled">Enable daily report</Label>
                    <button
                      id="dr-enabled"
                      onClick={() => handleConfigChange('daily_report_enabled', !systemConfig.daily_report_enabled)}
                      className={`relative w-11 h-6 rounded-full transition-colors ${systemConfig.daily_report_enabled ? 'bg-primary' : 'bg-slate-200'}`}
                      data-testid="daily-report-toggle"
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${systemConfig.daily_report_enabled ? 'translate-x-5' : ''}`} />
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="dr-hour" className="whitespace-nowrap">Send at (UTC hour)</Label>
                    <Input id="dr-hour" type="number" min="0" max="23" className="w-20"
                      value={systemConfig.daily_report_hour}
                      onChange={(e) => handleConfigChange('daily_report_hour', e.target.value)}
                      data-testid="daily-report-hour" />
                  </div>
                  <Button variant="outline" size="sm" onClick={handleSendReportNow} disabled={sendingReport} data-testid="send-report-now-btn">
                    {sendingReport ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />}
                    Send today's report now
                  </Button>
                </CardContent>
              </Card>

              {/* Gift Voucher Campaign */}
              <Card data-testid="voucher-campaign-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base"><Gift className="h-4 w-4" /> Gift Voucher Campaign</CardTitle>
                  <CardDescription>Auto-nudge customers to gift vouchers on paydays (25th) &amp; holidays — email + WhatsApp.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="vc-enabled">Enable campaign</Label>
                    <button
                      id="vc-enabled"
                      onClick={() => handleConfigChange('voucher_campaign_enabled', !systemConfig.voucher_campaign_enabled)}
                      className={`relative w-11 h-6 rounded-full transition-colors ${systemConfig.voucher_campaign_enabled ? 'bg-primary' : 'bg-slate-200'}`}
                      data-testid="voucher-campaign-toggle"
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${systemConfig.voucher_campaign_enabled ? 'translate-x-5' : ''}`} />
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="vc-hour" className="whitespace-nowrap">Send at (UTC hour)</Label>
                    <Input id="vc-hour" type="number" min="0" max="23" className="w-20"
                      value={systemConfig.voucher_campaign_hour}
                      onChange={(e) => handleConfigChange('voucher_campaign_hour', e.target.value)}
                      data-testid="voucher-campaign-hour" />
                  </div>
                  <Button variant="outline" size="sm" onClick={handleSendCampaignNow} disabled={sendingCampaign} data-testid="send-campaign-now-btn">
                    {sendingCampaign ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Gift className="h-4 w-4 mr-1" />}
                    Send a nudge now
                  </Button>
                </CardContent>
              </Card>

              {/* Weekly Menu Reminder */}
              <Card data-testid="weekly-reminder-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base"><Bell className="h-4 w-4" /> Weekly Menu Reminder</CardTitle>
                  <CardDescription>Every Monday morning, email + WhatsApp opted-in customers this week's meals so orders start early.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="wr-enabled">Enable Monday reminder</Label>
                    <button
                      id="wr-enabled"
                      onClick={() => handleConfigChange('weekly_reminder_enabled', !systemConfig.weekly_reminder_enabled)}
                      className={`relative w-11 h-6 rounded-full transition-colors ${systemConfig.weekly_reminder_enabled ? 'bg-primary' : 'bg-slate-200'}`}
                      data-testid="weekly-reminder-toggle"
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${systemConfig.weekly_reminder_enabled ? 'translate-x-5' : ''}`} />
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="wr-hour" className="whitespace-nowrap">Send at (UTC hour)</Label>
                    <Input id="wr-hour" type="number" min="0" max="23" className="w-20"
                      value={systemConfig.weekly_reminder_hour}
                      onChange={(e) => handleConfigChange('weekly_reminder_hour', e.target.value)}
                      data-testid="weekly-reminder-hour" />
                  </div>
                  <Button variant="outline" size="sm" onClick={handleSendWeeklyNow} disabled={sendingWeekly} data-testid="send-weekly-reminder-btn">
                    {sendingWeekly ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Bell className="h-4 w-4 mr-1" />}
                    Send this week's menu now
                  </Button>
                </CardContent>
              </Card>

              {/* Paystack Webhook Health */}
              <Card data-testid="webhook-health-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base"><CreditCard className="h-4 w-4" /> Paystack Webhook</CardTitle>
                  <CardDescription>Set this URL in Paystack so payments & top-ups credit instantly.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {webhookHealth ? (
                    <>
                      <div className="flex items-center gap-2">
                        {webhookHealth.healthy
                          ? <Badge className="bg-green-100 text-green-700 border-0"><CheckCircle className="h-3 w-3 mr-1" /> Receiving events</Badge>
                          : <Badge className="bg-amber-100 text-amber-700 border-0"><AlertCircle className="h-3 w-3 mr-1" /> No recent events</Badge>}
                        <span className="text-xs text-muted-foreground">{webhookHealth.events_received_7d} in last 7 days</span>
                      </div>
                      <div className="bg-muted/40 rounded-lg p-2 text-xs font-mono break-all" data-testid="webhook-url">{webhookHealth.expected_webhook_url}</div>
                      <p className="text-xs text-muted-foreground">{webhookHealth.instructions}</p>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">Loading…</p>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-800">Security Notice</h4>
                  <p className="text-sm text-amber-700">
                    API keys are encrypted and stored securely. Only Super Admins can view and modify these settings.
                    All changes are logged in the audit trail. Payment confirmations are only processed via secure webhooks.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <Button
                onClick={handleSaveConfig}
                disabled={savingConfig}
                className="bg-primary hover:bg-primary-600"
                data-testid="save-config-btn"
              >
                <Key className="h-4 w-4 mr-2" />
                {savingConfig ? 'Saving...' : 'Save Configuration'}
              </Button>
            </div>
            </div>{/* end space-y-6 */}
          </TabsContent>
        )}

        {/* Production Setup Tab */}
        {isSuperAdmin && (
          <TabsContent value="production">
            <ProductionSetupTab token={token} apiUrl={API_URL} />
          </TabsContent>
        )}

        {/* Notifications Tab */}
        {isSuperAdmin && (
          <TabsContent value="notifications">
            <NotificationSettingsTab token={token} />
          </TabsContent>
        )}

        {/* Security Tab */}
        {isSuperAdmin && (
          <TabsContent value="security">
            <div className="space-y-6">
              <BlockedUsersTab token={token} />
              <LockedAccountsTab token={token} />
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
